import { DateFormatter } from '@repo/toolkit/date'
import ActionButton from '@repo/ui/action-button'
import Badge, { BadgeProps } from '@repo/ui/badge'
import Button from '@repo/ui/button'
import Checkbox from '@repo/ui/checkbox'
import DataTable, { Column, PaginationInput } from '@repo/ui/data-table'
import { EyeOpenIcon, PenIcon, SyncIcon, UploadIcon } from '@repo/ui/icons'
import Modal from '@repo/ui/modal'
import SwitchButtons from '@repo/ui/switch-buttons'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@repo/ui/tooltip'
import Typography from '@repo/ui/typography'
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'
import { WretchError } from 'wretch/resolver'

import { getEndOfJourneyScanUploadLimits } from '../../../api/end-of-journey-scans/get-end-of-journey-scan-upload-limits'
import { getEndOfJourneyScans } from '../../../api/end-of-journey-scans/get-end-of-journey-scans'
import { resolveEndOfJourneyScanItem } from '../../../api/end-of-journey-scans/resolve-end-of-journey-scan-item'
import { retryEndOfJourneyScanItem } from '../../../api/end-of-journey-scans/retry-end-of-journey-scan-item'
import { reuploadEndOfJourneyScanItem } from '../../../api/end-of-journey-scans/reupload-end-of-journey-scan-item'
import { upsertEnrollmentEndOfJourneyQuestionnaire } from '../../../api/end-of-journey-scans/upsert-enrollment-end-of-journey-questionnaire'
import { getSignedUrl } from '../../../api/media/secure-upload'
import { useModalManager } from '../../../lib/hooks/use-modal-manager'
import useTranslate from '../../../lib/hooks/use-translate'
import {
  EndOfJourneyScanFailureEnum,
  endOfJourneyScanFailureMapping,
  EndOfJourneyScanItemStatusEnum,
  endOfJourneyScanItemStatusMapping,
  endOfJourneyScanUncertainTypeMapping
} from '../../../lib/types/enums/end-of-stay-template/end-of-journey-scan.enum'
import {
  EndOfJourneyAnswerInput,
  EndOfJourneyScanItem,
  EndOfJourneyScanReadingStatus
} from '../../../lib/types/models/end-of-journey-scan/end-of-journey-scan'
import {
  extractAllErrorMessages,
  showAxiosError
} from '../../../lib/utils/functions/misc.functions'
import { ReviewLoadError } from '../ols/centers/center-tab-views/center-end-of-stay-appreciations/review-rating-pill'
import EndOfJourneyQuestionnaireForm from './end-of-journey-questionnaire-form'
import { isPdf, isReadingPaused, uncertainReason } from './end-of-journey-scan.utils'

type ItemsTab = EndOfJourneyScanItemStatusEnum | 'ALL'

const TABS: ItemsTab[] = [
  'ALL',
  EndOfJourneyScanItemStatusEnum.TO_PROCESS,
  EndOfJourneyScanItemStatusEnum.PROCESSING,
  EndOfJourneyScanItemStatusEnum.PROCESSED,
  EndOfJourneyScanItemStatusEnum.FAILED
]

const youthLabel = (item: EndOfJourneyScanItem) =>
  item.enrollment
    ? [
        item.enrollment.enrollmentNumber,
        item.enrollment.childFirstName,
        item.enrollment.childLastName
      ]
        .filter(Boolean)
        .join(' - ')
    : (item.printedEnrollmentNumber ?? '-')

/** Text of a confirmation modal: plain body text, centred like the title (no subtitle badge). */
const ConfirmationText = ({ text }: { text: string }) => (
  <div className="px-5 pt-3 text-center">
    <Typography variant="secondary-body">{text}</Typography>
  </div>
)

// A badge of the "Réponses incertaines" column, listing its questions (one per line) on hover
const QuestionsBadge = ({ badge, lines }: { badge: BadgeProps; lines: string[] }) => {
  if (lines.length === 0) return <Badge {...badge} />
  return (
    <TooltipProvider delayDuration={0}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge {...badge} />
        </TooltipTrigger>
        <TooltipContent
          align="start"
          className="max-w-md max-h-48 overflow-y-auto bg-white-hover border border-grey-light rounded-sm p-3 py-2"
        >
          <div className="flex flex-col items-start gap-1">
            {lines.map((line, index) => (
              <Typography key={index} variant="tag">
                {line}
              </Typography>
            ))}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

type ManualEntry = { id: number; enrollmentId: number; answers: EndOfJourneyAnswerInput[] }

type Props = {
  // The reading status sent with the list, for the page's banner
  onReadingStatusChange?: (readingStatus: EndOfJourneyScanReadingStatus | undefined) => void
}

const EndOfJourneyScanItemsTable = ({ onReadingStatusChange }: Props) => {
  const { t } = useTranslate()
  const queryClient = useQueryClient()
  // Manual entry refused because the youth already has a questionnaire: waits for confirmation
  const [replaceEntry, setReplaceEntry] = useState<ManualEntry>()
  // Relaunch ("Relancer", or "Remplacer" with force) waiting for confirmation
  const [retryTarget, setRetryTarget] = useState<{ item: EndOfJourneyScanItem; force: boolean }>()
  const [tab, setTab] = useState<ItemsTab>('ALL')
  const [retried, setRetried] = useState(false)
  const [pagination, setPagination] = useState<PaginationInput>({ pageIndex: 0, pageSize: 20 })
  const [file, setFile] = useState<File>()
  const fileInputRef = useRef<HTMLInputElement>(null)

  const { modalState, selectedData, toggleModal, closeModel } = useModalManager<
    'preview' | 'reupload' | 'manual' | 'correct',
    EndOfJourneyScanItem
  >(['preview', 'reupload', 'manual', 'correct'])

  const { data, isFetching, error, errorUpdateCount, isPlaceholderData, refetch } = useQuery({
    queryKey: ['end-of-journey-scans', tab, retried, pagination.pageIndex, pagination.pageSize],
    queryFn: () =>
      getEndOfJourneyScans({
        status: tab === 'ALL' ? undefined : tab,
        retried: retried || undefined,
        page: pagination.pageIndex,
        limit: pagination.pageSize
      }),
    placeholderData: keepPreviousData,
    // Poll while the backend still has questionnaires to process or processing (read in batches
    // every few minutes), and after a failed load so that the list comes back on its own
    refetchInterval: (query) =>
      query.state.status === 'error' || query.state.data?.hasActiveProcessing ? 30000 : false
  })
  // The rows of the previous tab or page are kept while the next one loads, never for a list that
  // already failed to load (polling, "Réessayer"): they are not the rows of this list
  const list = isPlaceholderData && errorUpdateCount > 0 ? undefined : data
  // A failed load stays on screen while it is loaded again: react-query clears the error of a
  // list without rows as soon as it is fetched again
  const loadFailed = !!error || (isFetching && !list && errorUpdateCount > 0)

  const readingStatus = list?.readingStatus
  const readingPaused = isReadingPaused(readingStatus)
  useEffect(() => {
    onReadingStatusChange?.(readingStatus)
  }, [readingStatus, onReadingStatusChange])

  // The scanned PDF, shown in the preview modal (signed URL from the backend)
  const {
    data: preview,
    isFetching: isPreviewLoading,
    isError: isPreviewError
  } = useQuery({
    queryKey: ['end-of-journey-scan-preview', selectedData?.uploadId],
    // null when the file is not available (still in the virus scan, or rejected by it)
    queryFn: async () =>
      ((await getSignedUrl(selectedData!.uploadId)) as { url: string } | null) ?? null,
    enabled: modalState.preview && !!selectedData,
    // Signed URLs expire: fetched again on each opening
    gcTime: 0
  })

  // Rows of the reading saved blank, to check on the scan: uncertain ones and unanswered ones
  const correctNote = ({ ambiguousCount, blankCount }: EndOfJourneyScanItem) => {
    if (ambiguousCount > 0 && blankCount > 0)
      return t('end.of.journey.scans.correct.note.both', {
        ambiguous: ambiguousCount,
        blank: blankCount
      })
    if (ambiguousCount > 0)
      return t('end.of.journey.scans.correct.note', { number: ambiguousCount })
    if (blankCount > 0) return t('end.of.journey.scans.correct.note.blank', { number: blankCount })
    return undefined
  }

  const { data: limits } = useQuery({
    queryKey: ['end-of-journey-scan-upload-limits'],
    queryFn: getEndOfJourneyScanUploadLimits
  })
  const fileNotPdf = !!file && !isPdf(file)
  const fileTooLarge = !!file && !!limits && file.size > limits.maxFileBytes

  // The re-scan file is cleared so that the same file can be picked again
  const resetReuploadFile = () => {
    setFile(undefined)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const counts = list?.counts
  const tabLabel = (value: ItemsTab) => {
    if (value === 'ALL') return t('end.of.journey.scans.tab.all')
    const label = t(endOfJourneyScanItemStatusMapping[value].label)
    return counts ? `${label} (${counts[value]})` : label
  }

  const { mutate: retry, isPending: isRetrying } = useMutation({
    mutationFn: retryEndOfJourneyScanItem,
    onError: showAxiosError,
    onSuccess: () => {
      if (readingPaused) toast.warning(t('end.of.journey.scans.retry.success.paused'))
      else toast.success(t('end.of.journey.scans.retry.success'))
      setRetryTarget(undefined)
      refetch()
    }
  })

  const { mutate: reupload, isPending: isReuploading } = useMutation({
    mutationFn: reuploadEndOfJourneyScanItem,
    onError: showAxiosError,
    onSuccess: () => {
      if (readingPaused) toast.warning(t('end.of.journey.scans.reupload.success.paused'))
      else toast.success(t('end.of.journey.scans.reupload.success'))
      resetReuploadFile()
      closeModel('reupload')
      refetch()
    }
  })

  const { mutate: resolve, isPending: isResolving } = useMutation({
    mutationFn: resolveEndOfJourneyScanItem,
    onError: (error, input) => {
      // The youth already has a questionnaire: replaced only once the admin confirms
      if ((error as WretchError).status === 409 && !input.force) return setReplaceEntry(input)
      showAxiosError(error)
    },
    onSuccess: () => {
      toast.success(t('end.of.journey.scans.resolve.success'))
      setReplaceEntry(undefined)
      closeModel('manual')
      refetch()
    }
  })

  const { mutate: correct, isPending: isCorrecting } = useMutation({
    mutationFn: upsertEnrollmentEndOfJourneyQuestionnaire,
    onError: showAxiosError,
    onSuccess: (_, input) => {
      toast.success(t('end.of.journey.scans.correct.success'))
      // The next "Corriger" loads the saved answers again, never the ones before this save
      queryClient.removeQueries({ queryKey: ['end-of-journey-questionnaire', input.enrollmentId] })
      closeModel('correct')
      refetch()
    }
  })

  const columns: Column<EndOfJourneyScanItem>[] = [
    {
      accessorKey: 'fileName',
      header: t('end.of.journey.scans.column.file'),
      cell: ({ row }) => (
        <div className="flex flex-col gap-1">
          <span>{row.original.fileName}</span>
          {row.original.matchedCenter && (
            <span className="text-sm">{row.original.matchedCenter.name}</span>
          )}
        </div>
      )
    },
    {
      accessorKey: 'status',
      header: t('end.of.journey.scans.column.status'),
      cell: ({ row }) => {
        const item = row.original
        const status = endOfJourneyScanItemStatusMapping[item.status]
        return (
          <div className="flex flex-col items-start gap-1.5 py-2.5">
            <Badge variant={status.variant} text={t(status.label)} />
            {item.stuck && <Badge variant="grey" text={t('end.of.journey.scans.stuck')} />}
            {item.failureReason && (
              <Badge
                variant="danger"
                text={t(endOfJourneyScanFailureMapping[item.failureReason])}
              />
            )}
            {item.replaceExisting && (
              <span className="text-sm">{t('end.of.journey.scans.replace.existing')}</span>
            )}
            {item.failureMessage && <span className="text-sm">{item.failureMessage}</span>}
          </div>
        )
      }
    },
    {
      accessorKey: 'enrollment',
      header: t('end.of.journey.scans.column.youth'),
      cell: ({ row }) => (
        <div className="flex flex-col gap-1">
          <span>{youthLabel(row.original)}</span>
          {/* Not identified yet: the template read on the copy (QR code) */}
          {!row.original.enrollment && row.original.printedTemplateId && (
            <span className="text-sm">
              {t('end.of.journey.scans.template.id', { id: row.original.printedTemplateId })}
            </span>
          )}
        </div>
      )
    },
    {
      accessorKey: 'attempts',
      header: t('end.of.journey.scans.column.attempts'),
      cell: ({ row }) => `${row.original.attempts} / ${row.original.maxAttempts}`
    },
    {
      accessorKey: 'ambiguousCount',
      header: t('end.of.journey.scans.column.ambiguous'),
      cell: ({ row }) =>
        row.original.ambiguousCount > 0 || row.original.blankCount > 0 ? (
          <div className="flex flex-wrap gap-1.5 py-2.5">
            {row.original.ambiguousCount > 0 && (
              <QuestionsBadge
                badge={{
                  variant: 'warning',
                  text: t('end.of.journey.scans.ambiguous.count', {
                    number: row.original.ambiguousCount
                  })
                }}
                lines={row.original.uncertainQuestions.map(
                  (question) =>
                    `${question.label} : ${uncertainReason(
                      t(endOfJourneyScanUncertainTypeMapping[question.type]),
                      question.options
                    )}`
                )}
              />
            )}
            {row.original.blankCount > 0 && (
              <QuestionsBadge
                badge={{
                  variant: 'grey',
                  text: t('end.of.journey.scans.blank.count', { number: row.original.blankCount })
                }}
                lines={row.original.blankQuestions.map((question) => question.label)}
              />
            )}
          </div>
        ) : (
          '-'
        )
    },
    {
      accessorKey: 'createdAt',
      header: t('end.of.journey.scans.column.sent.at'),
      cell: ({ row }) =>
        `${DateFormatter.ddMMYYYY_HHMM(row.original.createdAt)}${
          row.original.uploadedBy
            ? ` - ${row.original.uploadedBy.firstName} ${row.original.uploadedBy.lastName}`
            : ''
        }`
    },
    {
      accessorKey: 'id',
      header: '',
      cell: ({ row }) => {
        const item = row.original
        return (
          <div className="flex gap-2 justify-end">
            <ActionButton
              variant="grey"
              icon={EyeOpenIcon}
              tooltipText={t('end.of.journey.scans.action.preview')}
              onClick={() => toggleModal('preview', item)}
            />
            {item.actionable && (
              <>
                <ActionButton
                  variant="grey"
                  icon={UploadIcon}
                  tooltipText={t('end.of.journey.scans.action.reupload')}
                  onClick={() => toggleModal('reupload', item)}
                />
                <ActionButton
                  variant="grey"
                  icon={SyncIcon}
                  tooltipText={t('end.of.journey.scans.action.retry')}
                  disabled={isRetrying}
                  onClick={() => setRetryTarget({ item, force: false })}
                />
                {item.failureReason === EndOfJourneyScanFailureEnum.ALREADY_RECEIVED && (
                  <Button
                    size="xsmall"
                    variant="outline"
                    label={t('end.of.journey.scans.action.replace')}
                    disabled={isRetrying}
                    onClick={() => setRetryTarget({ item, force: true })}
                  />
                )}
                <ActionButton
                  variant="primary"
                  icon={PenIcon}
                  tooltipText={t('end.of.journey.scans.action.manual')}
                  onClick={() => toggleModal('manual', item)}
                />
              </>
            )}
            {item.status === EndOfJourneyScanItemStatusEnum.PROCESSED && item.enrollment && (
              <ActionButton
                variant="primary"
                icon={PenIcon}
                tooltipText={t('end.of.journey.scans.action.correct')}
                onClick={() => toggleModal('correct', item)}
              />
            )}
          </div>
        )
      }
    }
  ]

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-4">
        <SwitchButtons<ItemsTab>
          items={TABS.map((value) => ({ value, label: tabLabel(value) }))}
          value={tab}
          onChange={(value) => {
            setTab(value)
            setPagination((prev) => ({ ...prev, pageIndex: 0 }))
          }}
        />
        <Checkbox
          label={t('end.of.journey.scans.filter.retried')}
          checked={retried}
          onChange={(checked) => {
            setRetried(checked)
            setPagination((prev) => ({ ...prev, pageIndex: 0 }))
          }}
        />
        {/* The list could not be loaded: the backend's reason, the rows of the last load if any */}
        {loadFailed && (
          <div className="flex items-center gap-2">
            {!!list && error && <ReviewLoadError error={error} />}
            <Button
              size="xsmall"
              variant="outline"
              label={t('end.of.journey.scans.load.retry')}
              loading={isFetching}
              onClick={() => refetch()}
            />
          </div>
        )}
      </div>

      <DataTable<EndOfJourneyScanItem>
        columns={columns}
        data={list?.data ?? []}
        rowCount={list?.total ?? 0}
        isLoading={isFetching && !list}
        paginated
        state={pagination}
        onStateChange={setPagination}
        noResults={
          error ? extractAllErrorMessages(error).join(' ') : t('end.of.journey.scans.no.results')
        }
      />

      {/* Scanned PDF */}
      <Modal
        icon={EyeOpenIcon}
        isOpen={modalState.preview}
        title={selectedData?.fileName ?? t('end.of.journey.scans.preview.title')}
        size="large"
        hideActions
        onCancel={() => closeModel('preview')}
      >
        {isPreviewLoading ? (
          <div className="p-4">{t('end.of.journey.scans.preview.loading')}</div>
        ) : isPreviewError || !preview?.url ? (
          <div className="p-4">{t('end.of.journey.scans.preview.unavailable')}</div>
        ) : (
          <iframe
            src={preview.url}
            width="100%"
            height="600px"
            title={t('end.of.journey.scans.preview.title')}
          />
        )}
      </Modal>

      {/* Re-upload */}
      <Modal
        icon={UploadIcon}
        isOpen={modalState.reupload}
        title={t('end.of.journey.scans.reupload.title')}
        hideActions
        onCancel={() => {
          resetReuploadFile()
          closeModel('reupload')
        }}
      >
        <div className="flex flex-col gap-4 p-4">
          <p className="text-sm">{t('end.of.journey.scans.reupload.help')}</p>
          <input
            ref={fileInputRef}
            type="file"
            accept="application/pdf"
            className="hidden"
            onChange={(event) => setFile(event.target.files?.[0])}
          />
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              icon={UploadIcon}
              label={t('end.of.journey.scans.reupload.choose.file')}
              onClick={() => fileInputRef.current?.click()}
            />
            {file && <span className="text-sm">{file.name}</span>}
          </div>
          {fileNotPdf && (
            <span className="text-sm text-red">
              {t('end.of.journey.scans.reupload.error.not.pdf')}
            </span>
          )}
          {fileTooLarge && limits && (
            <span className="text-sm text-red">
              {t('end.of.journey.scans.reupload.error.too.large', {
                maxFileMb: limits.maxFileBytes / (1024 * 1024)
              })}
            </span>
          )}
          <div className="flex justify-end">
            <Button
              label={t('end.of.journey.scans.send')}
              loading={isReuploading}
              disabled={!file || !selectedData || fileNotPdf || fileTooLarge}
              onClick={() => file && selectedData && reupload({ id: selectedData.id, file })}
            />
          </div>
        </div>
      </Modal>

      {/* Manual entry of a failed questionnaire */}
      <Modal
        icon={PenIcon}
        isOpen={modalState.manual}
        title={t('end.of.journey.scans.action.manual')}
        size="large"
        hideActions
        onCancel={() => closeModel('manual')}
      >
        {modalState.manual && selectedData && (
          <EndOfJourneyQuestionnaireForm
            itemId={selectedData.id}
            initialEnrollment={
              selectedData.enrollment
                ? { id: selectedData.enrollment.id, label: youthLabel(selectedData) }
                : undefined
            }
            loading={isResolving}
            onCancel={() => closeModel('manual')}
            onSubmit={(input: { enrollmentId: number; answers: EndOfJourneyAnswerInput[] }) =>
              resolve({ id: selectedData.id, ...input })
            }
          />
        )}
      </Modal>

      {/* Relaunch of the reading ("Remplacer": overwrites the youth's questionnaire) */}
      <Modal
        variant="confirmation"
        isOpen={!!retryTarget}
        title={t(
          retryTarget?.force
            ? 'end.of.journey.scans.retry.replace.confirm.title'
            : 'end.of.journey.scans.retry.confirm.title'
        )}
        actionButtonsOptions={{
          confirmText: t(
            retryTarget?.force
              ? 'end.of.journey.scans.action.replace'
              : 'end.of.journey.scans.action.retry'
          ),
          cancelText: t('end.of.journey.scans.cancel'),
          onConfirm: () =>
            retryTarget && retry({ id: retryTarget.item.id, force: retryTarget.force })
        }}
        isLoading={isRetrying}
        onCancel={() => setRetryTarget(undefined)}
      >
        {retryTarget && (
          <ConfirmationText
            text={t(
              retryTarget.force
                ? 'end.of.journey.scans.retry.replace.confirm.text'
                : 'end.of.journey.scans.retry.confirm.text',
              { fileName: retryTarget.item.fileName }
            )}
          />
        )}
      </Modal>

      {/* Manual entry for a youth who already has a questionnaire */}
      <Modal
        variant="confirmation"
        isOpen={!!replaceEntry}
        title={t('end.of.journey.scans.replace.title')}
        actionButtonsOptions={{
          confirmText: t('end.of.journey.scans.action.replace'),
          cancelText: t('end.of.journey.scans.cancel'),
          onConfirm: () => replaceEntry && resolve({ ...replaceEntry, force: true })
        }}
        isLoading={isResolving}
        onCancel={() => setReplaceEntry(undefined)}
      >
        <ConfirmationText text={t('end.of.journey.scans.replace.subtitle')} />
      </Modal>

      {/* Correction of a saved questionnaire */}
      <Modal
        icon={PenIcon}
        isOpen={modalState.correct}
        title={t('end.of.journey.scans.correct.title')}
        size="large"
        hideActions
        onCancel={() => closeModel('correct')}
      >
        {modalState.correct && selectedData?.enrollment && (
          <EndOfJourneyQuestionnaireForm
            enrollment={{ id: selectedData.enrollment.id, label: youthLabel(selectedData) }}
            note={correctNote(selectedData)}
            uncertainQuestions={selectedData.uncertainQuestions}
            blankQuestions={selectedData.blankQuestions}
            loading={isCorrecting}
            onCancel={() => closeModel('correct')}
            onSubmit={correct}
          />
        )}
      </Modal>
    </div>
  )
}

export default EndOfJourneyScanItemsTable
